import { MessageSquare, FileText } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useRouter } from "next/navigation";

import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { Label } from "@/app/_module/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";

import { useGetSmsSettings } from "../hooks/queries";
import { updateSmsSettings } from "../services/sms-settings-service";
import type { SmsSettings, SmsTemplate } from "../types/sms-settings";
import { useQueryClient } from "@tanstack/react-query";
import { smsSettingsQueryKeys } from "../services/sms-settings-keys";

import { SelectTemplateButton } from "./SelectTemplateButton";
import { SMSTemplateManagement } from "./SMSTemplateManagement";

export const SMSTemplateSettings = function() {
  const { data: smsSettingsData, isLoading, error } = useGetSmsSettings();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const router = useRouter();

  // Get SMS settings sub-tab from URL
  const smsSettingsTab = searchParams.get("smsSettingsTab") || "settings";

  // Handle SMS settings sub-tab change
  const handleSmsSettingsTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("smsSettingsTab", value);
    router.replace(`/settings?tab=sms-settings&smsTab=templates&${params.toString()}`, { scroll: false });
  };

  const [smsSettings, setSmsSettings] = useState<SmsSettings>({
    autoReply: false,
    signature: "",
    templateId: "",
    deliveryReports: true,
    businessHoursOnly: true,
    includeUnsubscribeLink: true, // Always true - required for compliance
  });

  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<SmsSettings | null>(null);
  const [originalTemplateId, setOriginalTemplateId] = useState<string>("");

  // Track if we just saved to skip the next sync from smsSettingsData
  const skipNextSyncRef = useRef(false);
  // Last successfully-saved payload — used to dedupe redundant auto-saves so the
  // change → save → refetch → change cycle can't loop and spam the toast.
  const lastSavedPayloadRef = useRef<string | null>(null);

  // Update local state when data is loaded
  useEffect(() => {
    // Skip this sync if we just saved (to prevent loop)
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    // Handle both old and new data structures
    const settings = smsSettingsData?.smsSettings || (smsSettingsData as any)?.data?.smsSettings;

    if (settings) {
      // Only update state if values actually changed to prevent infinite loops
      setSmsSettings(prev => {
        const hasChanges = JSON.stringify(prev) !== JSON.stringify(settings);
        if (!hasChanges) return prev;
        return settings;
      });

      setOriginalSettings(prev => {
        const hasChanges = JSON.stringify(prev) !== JSON.stringify(settings);
        if (!hasChanges) return prev;
        return settings;
      });

      setOriginalTemplateId(prev => {
        const newTemplateId = settings.templateId || "";
        if (prev === newTemplateId) return prev;
        return newTemplateId;
      });
    }
  }, [smsSettingsData]);

  // Update selected template when data is loaded
  useEffect(() => {
    // Skip this sync if we just saved (to prevent loop)
    if (skipNextSyncRef.current) {
      return;
    }

    // Handle both old and new data structures
    const template = smsSettingsData?.template || (smsSettingsData as any)?.data?.template;

    if (template) {
      setSelectedTemplate(prev => {
        if (prev?.id === template.id) return prev;
        return template;
      });
    }
  }, [smsSettingsData]);

  // Check for changes and auto-save with debounce
  useEffect(() => {
    if (originalSettings && !isSaving) {
      // Compare settings excluding templateId (we check that separately)
      const { templateId: currentTemplateId, ...currentSettingsWithoutTemplate } = smsSettings;
      const { templateId: originalTemplateIdFromSettings, ...originalSettingsWithoutTemplate } = originalSettings;

      const settingsChanged = JSON.stringify(currentSettingsWithoutTemplate) !== JSON.stringify(originalSettingsWithoutTemplate);
      const templateChanged = smsSettings.templateId !== originalTemplateId;
      const hasChanges = settingsChanged || templateChanged;

      setHasChanges(hasChanges);

      // Auto-save when changes are detected (with debounce)
      if (hasChanges) {
        const timer = setTimeout(() => {
          handleSaveSmsSettings();
        }, 500); // 500ms debounce

        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [smsSettings, originalSettings, originalTemplateId, isSaving]);

  const handleTemplateSelect = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setSmsSettings(prev => ({
      ...prev,
      templateId: template.id,
    }));
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
    setSmsSettings(prev => ({
      ...prev,
      templateId: "",
    }));
  };

  const handleSaveSmsSettings = async () => {
    const updateData: SmsSettings = {
      templateId: smsSettings.templateId,
      signature: smsSettings.signature,
      autoReply: smsSettings.autoReply,
      deliveryReports: smsSettings.deliveryReports,
      businessHoursOnly: smsSettings.businessHoursOnly,
      includeUnsubscribeLink: true, // Always true - required for compliance
    };

    // Dedupe: if this exact payload was already saved, do nothing. Without this,
    // the auto-save effect could re-fire (a server-sync producing a phantom diff)
    // and re-save the same settings over and over — spamming the "Saved" toast.
    const payloadKey = JSON.stringify(updateData);
    if (payloadKey === lastSavedPayloadRef.current) {
      setHasChanges(false);
      return;
    }

    setIsSaving(true);
    try {
      await updateSmsSettings(updateData);
      lastSavedPayloadRef.current = payloadKey;

      // Skip the next sync from smsSettingsData to prevent loop
      skipNextSyncRef.current = true;

      // Invalidate and refetch SMS settings to get the latest data
      await queryClient.invalidateQueries({ queryKey: smsSettingsQueryKeys.settings() });

      // Update original settings to reflect the new state
      setOriginalSettings(smsSettings);
      setOriginalTemplateId(smsSettings.templateId || "");
      setHasChanges(false);

      // No success toast on auto-save — the inline "All changes saved" indicator
      // is the feedback. A toast per save spammed the notification stack.
    } catch (error) {
      // Reset the skip flag on error so we can sync again
      skipNextSyncRef.current = false;

      toast({
        title: "Save Failed",
        description: "Failed to save SMS settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            SMS Templates & Settings
          </CardTitle>
          <CardDescription>
            Loading SMS settings...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            SMS Templates & Settings
          </CardTitle>
          <CardDescription>
            Error loading SMS settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">
            Failed to load SMS settings. Please try again.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle both old and new data structures for template reference
  const template = smsSettingsData?.template || (smsSettingsData as any)?.data?.template;

  return (
    <Tabs value={smsSettingsTab} onValueChange={handleSmsSettingsTabChange} className="space-y-6">
      <TabsList className="inline-flex w-full">
        <TabsTrigger value="settings" className="flex-1 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          SMS Settings
        </TabsTrigger>
        <TabsTrigger value="templates" className="flex-1 flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Manage Templates
        </TabsTrigger>
      </TabsList>

      <TabsContent value="settings">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              SMS Settings
            </CardTitle>
            <CardDescription>
              Configure your SMS messaging preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="templateSelect">Default SMS Template</Label>
                <div className="flex gap-2">
                  <SelectTemplateButton
                    className="w-auto"
                    onTemplateSelect={handleTemplateSelect}
                    selectedTemplateId={selectedTemplate?.id || smsSettings.templateId}
                  />
                  {(selectedTemplate || smsSettings.templateId) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearTemplate}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="defaultTemplate">Template Preview</Label>
                <textarea
                  readOnly
                  className="w-full min-h-[120px] p-3 border rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  id="defaultTemplate"
                  placeholder="Select a template to preview its content..."
                  value={selectedTemplate?.content || template?.content || ""}
                />
                <p className="text-sm text-muted-foreground">
                  {selectedTemplate ? (
                    <React.Fragment>
                      Template: {selectedTemplate.name} ({selectedTemplate.category})
                      {selectedTemplate.id !== template?.id && (
                        <span className="text-orange-600 ml-2">(Changed)</span>
                      )}
                    </React.Fragment>
                  ) : template ? (
                    `Current template: ${template.name} (${template.category})`
                  ) : (
                    "No template selected"
                  )}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="signature">SMS Signature</Label>
              <textarea
                className="w-full min-h-[80px] p-3 border rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                id="signature"
                placeholder="Enter your SMS signature..."
                value={smsSettings.signature}
                onChange={(e) =>
                  setSmsSettings((prev) => ({ ...prev, signature: e.target.value }))
                }
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={smsSettings.autoReply}
                  id="autoReply"
                  onCheckedChange={(checked) =>
                    setSmsSettings((prev) => ({ ...prev, autoReply: !!checked }))
                  }
                />
                <Label className="text-sm" htmlFor="autoReply">
                  Enable auto-reply
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={smsSettings.businessHoursOnly}
                  id="businessHours"
                  onCheckedChange={(checked) =>
                    setSmsSettings((prev) => ({
                      ...prev,
                      businessHoursOnly: !!checked,
                    }))
                  }
                />
                <Label className="text-sm" htmlFor="businessHours">
                  Business hours only
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={smsSettings.deliveryReports}
                  id="deliveryReports"
                  onCheckedChange={(checked) =>
                    setSmsSettings((prev) => ({
                      ...prev,
                      deliveryReports: !!checked,
                    }))
                  }
                />
                <Label className="text-sm" htmlFor="deliveryReports">
                  Delivery reports
                </Label>
              </div>

            </div>

            <div className="flex items-center gap-2">
              {isSaving && (
                <span className="text-sm text-muted-foreground">Saving...</span>
              )}
              {!isSaving && !hasChanges && (
                <span className="text-sm text-green-600">All changes saved</span>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="templates">
        <SMSTemplateManagement />
      </TabsContent>
    </Tabs>
  );
};
